import clsx from 'clsx';
import { memo, ReactNode, useCallback, useEffect, useState } from 'react';
import { ControllerFontType, makeStyles } from '@bymason-private/controller-v1-theme-provider';
import {
  Box,
  ControllerAlert,
  Divider,
  LinearProgress,
  Text,
} from '@bymason-private/legacy-pattern-components';
import { useRouter } from '@bymason-private/route-provider';
import LeftNav, { ControllerNavAction } from '../../common/left-nav';
import { routes } from '../../router';
import { NewControllerPromoBanner } from '../legacy-controller-promo-popup';
import { LegacyControllerDeprecationBanner } from '../new-controller-deprecation-popup';
import { RequestDeviceModal, useToast } from '@bymason-private/controller-shared-widgets';
import { useCheckFeatureFlag } from '../../global-state';

const ROUTE_REGEX = /projects\/.*\/build/;

interface LayoutProps {
  busy?: boolean;
  contentContainerClass?: string;
  children: ReactNode;
  divider?: boolean;
  headerText: ReactNode;
  headerRightAdornment?: JSX.Element;
  subHeader?: ReactNode;
  stickyHeader?: boolean;
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'grid',
    gridTemplateColumns: 'max-content 1fr',
    backgroundColor: theme.palette.background.default,
    minHeight: '100vh',
  },
  linearProgressRoot: {
    left: 0,
    position: 'absolute',
    top: 0,
    width: '100%',
    zIndex: theme.zIndex.appBar + 10,
  },
  viewRoot: {
    display: 'grid',
    gridTemplateRows: 'max-content auto',
    backgroundColor: 'inherit',
  },
  staticHeader: {
    paddingTop: 10,
    backgroundColor: 'inherit',
  },
  stickyHeader: {
    position: 'sticky',
    top: 0,
    zIndex: theme.zIndex.appBar,
  },
  headerRoot: {
    display: 'grid',
    gridTemplateColumns: 'max-content auto max-content',
    columnGap: 40,
    width: '100%',
  },
  headerRightAdornment: {
    flex: '0 1 auto',
  },
  divider: {
    marginTop: theme.spacing(1.5),
  },
  dividerWithSubHeader: {
    marginTop: 0,
  },
}));

/**
 * The primary layout component for all Controller views.
 */
export default memo(function Layout({
  busy,
  children,
  contentContainerClass,
  divider = true,
  headerRightAdornment,
  headerText,
  subHeader,
  stickyHeader,
}: LayoutProps) {
  const classes = useStyles();
  const { navigate } = useRouter();
  const [setToastMessage, setToastSeverity] = useToast();
  const [isBuildEnabled] = useCheckFeatureFlag('controller_build');
  const [isAlertEnabled, alertBody] = useCheckFeatureFlag('controller-alert');
  const [showRequestForm, setShowRequestForm] = useState(false);
  const handleNavAction = (action: ControllerNavAction) => {
    switch (action) {
      case ControllerNavAction.AccountProfile:
        navigate(routes.Account);
        break;
      case ControllerNavAction.ResourcesByMason:
      case ControllerNavAction.ResourcesDocs:
      case ControllerNavAction.ResourcesQuickstart:
        location.href = getLegacyUrl(action);
        break;
      case ControllerNavAction.ResourcesContactSupport:
        window.zE('webWidget', 'show');
        break;

      case ControllerNavAction.ResourcesDevKits:
        setShowRequestForm(true);
        break;

      case ControllerNavAction.Artifacts:
        navigate(routes.Artifacts);
        break;
      case ControllerNavAction.Connectivity:
        navigate(routes.Connectivity);
        break;
      case ControllerNavAction.Devices:
        navigate(routes.Devices);
        break;
      case ControllerNavAction.Dashboard:
        navigate(routes.Dashboard);
        break;
      case ControllerNavAction.Groups:
        navigate(routes.Groups);
        break;
      case ControllerNavAction.Projects:
        navigate(routes.Projects);
        break;
      case ControllerNavAction.AccountUsers:
        navigate(routes.User);
        break;
      case ControllerNavAction.AccountApiKeys:
        navigate(routes.ApiKeys);
        break;
      case ControllerNavAction.AccountNotifications:
        navigate(routes.Notifications);
        break;
    }
  };

  const handleCloseRequestFrom = () => {
    setShowRequestForm(false);
  };

  const handleRequestFormSuccess = useCallback(() => {
    setToastMessage('Successfully submitted request to customer success.');
    setToastSeverity('success');
  }, [setToastMessage, setToastSeverity]);

  const handleRequestFormError = useCallback(() => {
    setToastMessage('Failed to submit request to customer success. Please try again later.');
    setToastSeverity('error');
  }, [setToastMessage, setToastSeverity]);

  useEffect(() => {
    if (!isBuildEnabled && ROUTE_REGEX.test(window.location.pathname)) {
      navigate(routes.Projects);
    }
  }, [isBuildEnabled, navigate]);

  return (
    <div className={classes.root}>
      <LeftNav onNavAction={handleNavAction} />
      <div className={classes.viewRoot}>
        {busy && <LinearProgress classes={{ root: classes.linearProgressRoot }} />}
        <Box
          className={clsx({
            [classes.staticHeader]: true,
            [classes.stickyHeader]: stickyHeader,
          })}
          sx={{ pt: '0px !important' }}
        >
          <LegacyControllerDeprecationBanner />
          <Box className={classes.headerRoot} sx={{ padding: (theme) => theme.spacing(1.5, 3, 0, 2.5) }}>
            <Text variant={ControllerFontType.Heading500H}>{headerText}</Text>
            <div>
              {isAlertEnabled && (
                <ControllerAlert severity='warning' sx={{ display: 'flex', py: 0.8 }} variant='outlined'>
                  {alertBody}
                </ControllerAlert>
              )}
            </div>
            <div>
              {!!headerRightAdornment && (
                <div className={classes.headerRightAdornment}>{headerRightAdornment}</div>
              )}
            </div>
          </Box>
          {subHeader && subHeader}
          {divider && (
            <Divider className={clsx(classes.divider, subHeader && classes.dividerWithSubHeader)} />
          )}
        </Box>
        <NewControllerPromoBanner />
        <Box className={contentContainerClass} sx={{ padding: (theme) => theme.spacing(0, 3, 1.25, 2.5) }}>
          {children}
        </Box>
        <RequestDeviceModal
          open={showRequestForm}
          onClose={handleCloseRequestFrom}
          onError={handleRequestFormError}
          onSuccess={handleRequestFormSuccess}
        />
      </div>
    </div>
  );
});

//
//
// helpers
//
//

function getLegacyUrl(path: string) {
  return `${location.origin.replace('3001', '3000')}/controller/${path}`;
}
